import { Connection } from './connection';
import { EditField } from './edit-field';
import { XItem } from './x-item';

import { MANIFEST_STORE_DELAY, type CoordinatorService } from './coordinator.service';
import { Temporal } from '@js-temporal/polyfill';
import { Subject, debounceTime } from 'rxjs';
import { ScriptDatabin } from './script-databin';
export type DTB = {
    id?: string;
    isOn?: boolean;
    title: string;
    connectionLs?: any[];
    _ts?: number;
};

type VALUEMAPKEYS = 'isOn' | 'title' | 'position' | '_ts';

export class Databin {
    id: string;
    ownerId?: string;
    cls = 'databin';

    activeXitemIdSet = new Set<string>();

    xitemMap: Map<string, XItem> = new Map<string, XItem>();

    private _valueMap = new Map<string, string | boolean | number>();
    private _editFieldMap = new Map<string, EditField>();

    private _connectionLs: Connection[] = [];
    // private _databinItemLs: DatabinItem[] = []

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    constructor(
        public coords: CoordinatorService,
        params: DTB,
    ) {
        this.id = params.id || this.coords.uniqueId();
        this._valueMap.set('_ts', params._ts || Temporal.Now.instant().toString());

        const title = params.title.trim();

        this._valueMap.set(
            'title',
            this.coords.utils$.uniqueTitle(
                title,
                this.coords.databin$.databinLs.map((x) => x.title),
            ),
        );
        this._valueMap.set('isOn', params.isOn !== false);
        this.coords.databin$.registerDatabin(this);
        this._markForManifestStore$.subscribe(() => this._manifestStore());
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    get ts(): number {
        return this._valueMap.get('_ts')! as number;
    }

    updateXitemMap(ls: XItem[]) {
        ls.forEach((xitm) => {
            this.xitemMap.set(xitm.id, xitm);
        });
        this.markForManifestStore();
    }

    // conPosition(c: Connection) {
    //     return this.connectionLs.indexOf(c)
    // }

    get title() {
        return this._valueMap.get('title')! as string;
    }

    get description() {
        return this._valueMap.get('description')! as string;
    }

    public get isOn() {
        return this._valueMap.get('isOn')! as boolean;
    }
    public set isOn(value: boolean) {
        this._valueMap.set('isOn', value);
        this.refresh();
        this.markForManifestStore();
    }

    get(tag: string) {
        return this._valueMap.get(tag);
    }

    set(tag: string, value: any) {
        this._valueMap.set(tag, value);
        this.markForManifestStore();
    }

    manifestDelete() {
        const ls = [
            {
                id: this.id,
                type: 'databin',
            },
            {
                id: this.id,
                type: 'databin-xitemLs',
            },
        ];

        this._connectionLs.forEach((c) => {
            ls.push({
                id: c.id,
                type: 'connection',
            });
            ls.push({
                id: c.id,
                type: 'connection-xitemIdLs',
            });
        });

        this.coords.manifest$.delete(ls);
    }

    private _manifestStore() {
        this.coords.manifest$.store({
            type: 'databin',
            id: this.id,
            valueMap: this._valueMap,
        });

        this.manifestXitemStore();
    }

    json(onlyActiveConnections = false) {
        let cLs = this._connectionLs;
        if (onlyActiveConnections) cLs = this._connectionLs.filter((c) => c.isOn);

        return {
            title: this.title,
            isOn: this.isOn,
            resources: cLs.map((c) => c.id),
        };
    }

    addXItem(xitem: XItem) {
        if (this.xitemMap.has(xitem.id)) return;
        this.xitemMap.set(xitem.id, xitem);
    }

    manifestXitemStore() {
        this.coords.manifest$.store({
            type: 'databin-xitemLs',
            id: this.id,
            valueMap: new Map<string, any[]>([
                ['xitemLs', Array.from(this.xitemMap.values()).map((xitm) => xitm.save())],
            ]),
        });
    }

    editField(tag: string) {
        let ef = this._editFieldMap.get(tag);
        if (!ef) {
            ef = new EditField(this.coords, tag, this);
            this._editFieldMap.set(tag, ef);
        }
        return ef;
    }

    get isCurrent() {
        return this.coords.databin$.current === this;
    }

    setCurrent() {
        this.coords.databin$.current = this;
    }

    sortConnections() {
        this._connectionLs.sort((a, b) => a.timestamp - b.timestamp);
    }

    get connectionLs() {
        return this._connectionLs;
    }

    get activeConnectionLs() {
        return this._connectionLs.filter((c) => c.isOn);
    }

    getConnectionTitle(title: string) {
        title = title.toLowerCase().trim();
        for (const c of this.connectionLs) {
            if (c.title.toLowerCase().trim() === title) return c;
        }
        return null;
    }

    hasConnection(c: Connection) {
        return this.connectionLs.includes(c);
    }

    addConnection(c: Connection) {
        const isStored = this._connectionLs.some(
            (existingConnection) => existingConnection.id === c.id,
        );

        if (isStored) {
            return;
        }

        this._connectionLs.push(c);
    }

    deleteConnection(c: Connection) {
        const i = this._connectionLs.findIndex(
            (existingConnection) => existingConnection.id === c.id,
        );
        this._connectionLs.splice(i, 1);
        this._pruneDatabin();
        c.isOn && this.refresh();
        this.markForManifestStore();
        this.coords.dataset$.preview.requestReduplicate();
    }

    private _pruneDatabin() {
        const s = new Set();
        this._connectionLs.forEach((c) => c.xitemIdLs.forEach((xitmId) => s.add(xitmId)));
        const deleteIdLs = Array.from(this.xitemMap.keys()).filter((xitmId) => !s.has(xitmId));
        deleteIdLs.forEach((xitmId) => this.xitemMap.delete(xitmId));
    }

    export() {
        this.scriptClass.export();
    }

    async duplicate() {
        await this.scriptClass.duplicate();
    }

    editTitle() {
        this.editField('title').edit();
    }

    refresh(skipGlobalRefresh = false): void {
        const idLs: string[][] = this._connectionLs.filter((c) => c.isOn).map((c) => c.xitemIdLs);

        this.activeXitemIdSet = new Set(idLs.flat());

        if (!skipGlobalRefresh) this._d$.refreshActiveXitemSet();
    }

    private get _d$() {
        return this.coords.databin$;
    }

    get size() {
        return this.activeXitemIdSet.size;
    }

    preview() {
        this.coords.resultList$.activateSubset({
            idSet: this.activeXitemIdSet,
            title: this.title,
            prefix: this.coords.text('databin'),
            icon: 'databin',
            class: 'selection-in-general',
            back: () => this.coords.stage$.set('databin'),
            stage: 'preview',
        });
    }

    deleteDialog() {
        this.coords.display$.delete$.next(this);
    }

    get deleteInfo() {
        return `
            <div>
            <div class='delete_info_sub'>
            <h2>${this.coords.text('Type')}:</h2>
            <span>
            ${this.coords.text('Databin')}
            </span>
            </div>
            <div class='delete_info_sub'>
            <h2>${this.coords.text('Title')}:</h2>
            <span>
            ${this.title}
            </span>
            </div>
            <div class='delete_info_sub'>
            <h2>${this.coords.text('Size')}:</h2>
            <span>
            ${this.size}
            </span>
            </div>
            </div>
        `;
    }

    delete() {
        this._d$.delete(this);
        this._d$.refresh$.next();
        this.coords.dataset$.preview.requestReduplicate();
    }

    get helpHtml() {
        const ls: string[] = [];
        ls.push(`<b>${this.coords.text('Size-includingDuplicates')}: </b>${this.size}`);
        ls.push(`<b>${this.coords.text('Resources')}: </b>${this.connectionLs.length}`);

        for (const x of this.activeXitemIdSet) {
            const dt = this.coords.dataset$.preview;
            const xitm = x && dt.get(x);
            xitm && ls.push(xitm.title);
            if (ls.length >= 7) {
                ls.push('...');
                break;
            }
        }
        return ls.map((x) => `<div class='one-line'>${x}</div>`).join('\n');
    }

    get scriptClass() {
        return new ScriptDatabin(this.coords, this);
    }
}
